import { useEffect } from "react";
import { Routes, Route, Navigate, Outlet } from "react-router-dom";
import { useAuth } from "./auth/AuthProvider";
import { useBranding } from "./lib/useBranding";
import { Layout } from "./components/Layout";
import { Loading } from "./components/ui";
import { Login } from "./pages/Login";
import { Overview } from "./pages/Overview";
import { Clients } from "./pages/Clients";
import { ClientDetail } from "./pages/ClientDetail";
import { Revenue } from "./pages/Revenue";
import { Settings } from "./pages/Settings";
import { Docs } from "./pages/Docs";

/** Apply admin-configurable favicon + document title app-wide. */
function useApplyBranding() {
  const { favicon, title, subtitle } = useBranding();
  useEffect(() => {
    document.title = `${title} — ${subtitle}`;
    if (!favicon) return;
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = favicon;
  }, [favicon, title, subtitle]);
}

export default function App() {
  useApplyBranding();
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route element={<AuthedShell />}>
        <Route index element={<Home />} />
        <Route path="clients" element={<AdminOnly />}>
          <Route index element={<Clients />} />
        </Route>
        <Route path="clients/:id" element={<ClientDetail />} />
        <Route path="revenue" element={<AdminOnly />}>
          <Route index element={<Revenue />} />
        </Route>
        <Route path="settings" element={<AdminOnly />}>
          <Route index element={<Settings />} />
        </Route>
        <Route path="docs" element={<Docs />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/** Requires a session; renders the app shell. */
function AuthedShell() {
  const { loading, session } = useAuth();
  if (loading) return <Loading label="Starting…" fullScreen />;
  if (!session) return <Navigate to="/login" replace />;
  return <Layout />;
}

/** Admin-only guard for nested routes. */
function AdminOnly() {
  const { me } = useAuth();
  if (me && me.role !== "admin") return <Navigate to="/" replace />;
  return <Outlet />;
}

/** Role-aware home: admins get the overview; clients go to their own detail. */
function Home() {
  const { me } = useAuth();
  if (!me) return <Loading fullScreen />;
  if (me.role === "client") {
    return me.clientId ? (
      <Navigate to={`/clients/${me.clientId}`} replace />
    ) : (
      <div className="text-sm text-muted">No client assigned to your account yet.</div>
    );
  }
  return <Overview />;
}
